import { useId, useMemo, useState } from 'react';
import { Alert, Card, CardContent, CardTitle, Input, LoadingSpinner } from '@/shared/components';
import { useDocumentVariablesQuery } from '../hooks/useDocumentVariablesQuery';
import type { DocumentVariable } from '../types/documentTemplates.types';
import { filterVariables, groupVariablesByNamespace } from '../utils/variableGroups';
import { variableSample } from '../utils/variableSample';

interface Props {
  onInsert: (token: string) => void;
}

function VariableEntry({ variable, onInsert }: { variable: DocumentVariable; onInsert: Props['onInsert'] }) {
  return (
    <li>
      <button
        type="button"
        className="w-full rounded-md border border-mp-border px-2.5 py-2 text-left hover:bg-mp-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mp-primary"
        aria-label={`Insert ${variable.label}`}
        onClick={() => onInsert(`{{${variable.key}}}`)}
      >
        <span className="flex items-baseline justify-between gap-2">
          <span className="font-medium">{variable.label}</span>
          <span className="truncate text-xs text-mp-muted">{variableSample(variable)}</span>
        </span>
        <span className="block font-mono text-xs text-mp-muted">{`{{${variable.key}}}`}</span>
      </button>
    </li>
  );
}

export function VariableReferencePanel({ onInsert }: Props) {
  const headingId = useId();
  const query = useDocumentVariablesQuery();
  const [search, setSearch] = useState('');
  const groups = useMemo(
    () => groupVariablesByNamespace(filterVariables(query.data ?? [], search)),
    [query.data, search],
  );

  return (
    <aside aria-label="Variable reference" className="flex min-h-0 flex-col">
      <Card ariaLabelledBy={headingId} className="flex min-h-0 flex-col">
        <CardContent className="flex min-h-0 flex-col gap-3 py-5">
          <CardTitle size="sm" id={headingId}>
            Variables
          </CardTitle>
          <Input
            type="search"
            aria-label="Search variables"
            placeholder="Search variables"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            hint="Select one to insert it at the cursor."
            hintId="variable-search-hint"
            aria-describedby="variable-search-hint"
          />
          {query.isLoading && <LoadingSpinner size="sm" message="Loading variables..." />}
          {query.isError && (
            <Alert variant="error">
              <span>Could not load the variable catalogue.</span>
            </Alert>
          )}
          {query.isSuccess && groups.length === 0 && <p className="text-sm text-mp-muted">No variables match.</p>}
          <div className="max-h-80 min-h-0 space-y-3 overflow-y-auto text-sm">
            {groups.map(({ namespace, variables }) => (
              <section key={namespace} aria-label={`${namespace} variables`}>
                <h3 className="mb-1.5 text-xs font-semibold capitalize text-mp-foreground">{namespace}</h3>
                <ul className="space-y-1.5">
                  {variables.map((variable) => (
                    <VariableEntry key={variable.key} variable={variable} onInsert={onInsert} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </CardContent>
      </Card>
    </aside>
  );
}
